import React, { useEffect, useState } from 'react';
import { COOKIE_CONSENT_STORAGE_KEY, CookieConsentCategories, getStoredCookieConsent, storeCookieConsent } from '../utils/legal';
import { Link } from 'react-router-dom';

type Props = {
  onConsentChange?: (consent: CookieConsentCategories) => void;
};

const defaultConsent: CookieConsentCategories = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false
};

const CookieConsent: React.FC<Props> = ({ onConsentChange }) => {
  const [consent, setConsent] = useState<CookieConsentCategories>(defaultConsent);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = getStoredCookieConsent();
    if (!saved) {
      setVisible(true);
    } else {
      setConsent(saved);
      onConsentChange?.(saved);
    }
  }, [onConsentChange]);

  const acceptAll = () => {
    const next: CookieConsentCategories = { necessary: true, preferences: true, analytics: true, marketing: true };
    setConsent(next);
    storeCookieConsent(next);
    setVisible(false);
    onConsentChange?.(next);
  };

  const rejectAll = () => {
    const next: CookieConsentCategories = { necessary: true, preferences: false, analytics: false, marketing: false };
    setConsent(next);
    storeCookieConsent(next);
    setVisible(false);
    onConsentChange?.(next);
  };

  const saveChoices = () => {
    storeCookieConsent(consent);
    setVisible(false);
    onConsentChange?.(consent);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50">
      <div className="max-w-5xl mx-auto m-4 p-4 rounded-lg bg-gray-900 text-white border border-gray-700 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-left">
            <h2 className="text-lg font-semibold">We use cookies</h2>
            <p className="text-sm text-gray-300">We use necessary cookies to make our site work. We'd also like to set optional cookies to help us improve it. Read our <Link to="/cookie-policy" className="text-red-400 underline">Cookie Policy</Link>.</p>
          </div>
          <div className="flex flex-col items-stretch gap-2 md:w-1/2">
            <div className="grid grid-cols-2 gap-2 text-sm bg-black/40 p-2 rounded">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked disabled />
                Necessary
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={consent.preferences} onChange={(e) => setConsent({ ...consent, preferences: e.target.checked })} />
                Preferences
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={consent.analytics} onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })} />
                Analytics
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={consent.marketing} onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })} />
                Marketing
              </label>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={rejectAll} className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700">Reject non-essential</button>
              <button onClick={saveChoices} className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700">Save choices</button>
              <button onClick={acceptAll} className="px-3 py-2 rounded bg-red-600 hover:bg-red-500">Accept all</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;





